<template>
  <div v-if="show" class="app-modal contributor-list-modal" role="presentation">
    <div class="app-modal__backdrop" @click="$emit('close')"></div>

    <section
      class="app-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contributor-list-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="contributor-list-modal-title">{{ text.list.title }}</h3>
          <p>{{ text.list.subtitle }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.common.close"
          @click="$emit('close')"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content">
        <div class="contributor-list-modal__body">
          <div v-if="summaryContributors.length === 0" class="contributor-list-modal__empty">
            {{ text.list.empty }}
          </div>

          <div
            v-for="contributor in summaryContributors"
            :key="contributor.key"
            class="contributor-list-row"
            :class="{ 'is-default': contributor.isDefault }"
          >
            <div class="contributor-list-row__main">
              <strong>
                {{ contributor.name }}
                <span v-if="contributor.isDefault" class="contributor-list-row__default-badge"><span>{{ text.common.defaultBadge }}</span></span>
              </strong>
            </div>

            <div class="contributor-list-row__meta">
              <span>
                <i class="fa fa-id-card-o" aria-hidden="true"></i>
                {{ contributor.rfc }}
              </span>
              <span>
                <i class="fa fa-file-text-o" aria-hidden="true"></i>
                {{ contributor.regimen }}
              </span>
              <span>
                <i class="fa fa-map-marker" aria-hidden="true"></i>
                {{ contributor.residence }}
              </span>
            </div>

            <div class="contributor-list-row__actions">
              <button
                type="button"
                class="app-table-icon-btn app-table-icon-btn--edit"
                :title="text.list.actions.edit"
                @click="$emit('edit-contributor', contributor.raw)"
              >
                <i class="fa fa-pencil" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="app-table-icon-btn app-table-icon-btn--delete"
                :title="text.list.actions.delete"
                @click="$emit('delete-contributor', contributor.raw)"
              >
                <i class="fa fa-trash" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>

        <footer class="app-modal__footer contributor-list-modal__footer">
          <div class="app-modal__footer-right">
            <button type="button" class="btn btn-light" @click="$emit('close')">{{ text.common.close }}</button>
          </div>
        </footer>
      </div>
    </section>
  </div>
</template>

<script>
import { formatCodeName, valueOrDash } from '@/utils/textValue';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('billingData');

export default {
  name: 'ContributorListModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    contributors: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'edit-contributor', 'delete-contributor'],
  data() {
    return {
      text,
    };
  },
  computed: {
    summaryContributors() {
      const list = Array.isArray(this.contributors) ? this.contributors.slice() : [];

      if (list.length === 0) {
        return [];
      }

      list.sort((left, right) => {
        const leftDefault = Number(left?.default || 0);
        const rightDefault = Number(right?.default || 0);

        if (leftDefault !== rightDefault) {
          return rightDefault - leftDefault;
        }

        const leftId = Number(left?.id || 0);
        const rightId = Number(right?.id || 0);

        return rightId - leftId;
      });

      const hasDefault = list.some((contributor) => Number(contributor?.default || 0) === 1);

      return list.map((contributor, index) => {
        const normalizedName = String(
          contributor?.name || contributor?.business_name || contributor?.fiscal_name || ''
        ).trim();
        const isDefault = Number(contributor?.default || 0) === 1 || (!hasDefault && index === 0);

        return {
          key: `${contributor?.id || index}-${normalizedName}`,
          name: valueOrDash(normalizedName),
          rfc: valueOrDash(contributor?.rfc || contributor?.tax_id),
          regimen: valueOrDash(
            formatCodeName(
              contributor?.regimen,
              contributor?.regimen_name,
              contributor?.regimen_label
            )
          ),
          residence: valueOrDash(this.buildFiscalResidence(contributor || {})),
          isDefault,
          raw: contributor,
        };
      });
    },
  },
  methods: {
    buildFiscalResidence(contributor) {
      const zipcode = String(contributor?.zipcode || '').trim();

      const streetSegments = [
        String(contributor?.street || '').trim(),
        String(contributor?.exterior || '').trim(),
        String(contributor?.interior || '').trim(),
      ].filter(Boolean);

      const addressSegments = [
        streetSegments.join(' '),
        String(contributor?.neighborhood || '').trim(),
        String(contributor?.municipality || '').trim(),
        String(contributor?.city || '').trim(),
        String(contributor?.state_name || contributor?.state || '').trim(),
        String(contributor?.country_name || contributor?.country || '').trim(),
      ].filter(Boolean);

      const fullAddress = addressSegments.join(', ');

      if (zipcode !== '' && fullAddress !== '') {
        return `${zipcode} - ${fullAddress}`;
      }

      if (zipcode !== '') {
        return zipcode;
      }

      return fullAddress;
    },
  },
};
</script>
